import { useCallback, useEffect, useMemo, useRef } from 'react';
import { driver, type Config, type DriveStep, type Driver } from 'driver.js';
import { createDefaultTutorialConfig } from '../design-system';
import { useStore } from '../context';
import { TutorialActions } from '../store/actions/tutorial';
import { backdropWaitingFc } from '../services';
import { queryFirstMatchingSelector } from '../utils';

export const useTutorial = (overrides?: Partial<Config>) => {
  const { dispatch } = useStore();
  const tutorialRef = useRef<Driver | null>(null);

  const syncTutorialState = useCallback(
    (isActive: boolean) => {
      dispatch({
        type: TutorialActions.SET_TUTORIAL,
        payload: { isActive },
      });
    },
    [dispatch],
  );

  const configWithStoreHooks = useMemo<Partial<Config>>(
    () => ({
      onDestroyStarted: (...args) => {
        overrides?.onDestroyStarted?.(...args);
      },
      onHighlighted: (element, step, opts) => {
        overrides?.onHighlighted?.(element, step, opts);
        syncTutorialState(true);
      },
      onDestroyed: (...args) => {
        overrides?.onDestroyed?.(...args);
        tutorialRef.current = null;
        syncTutorialState(false);
      },
      onCloseClick: (element, step, opts) => {
        overrides?.onCloseClick?.(element, step, opts);
        opts.driver.destroy();
      },
      onNextClick: (element, step, opts) => {
        if (typeof overrides?.onNextClick === 'function') {
          overrides.onNextClick(element, step, opts);
          return;
        }

        const nextClickEl = (step as any)?.onNextClickEl as string | undefined;
        if (typeof nextClickEl === 'string' && nextClickEl.trim()) {
          queryFirstMatchingSelector(nextClickEl)?.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true }),
          );
        }

        if (opts.driver.isLastStep()) {
          opts.driver.destroy();
          return;
        }

        if (!nextClickEl) {
          opts.driver.moveNext();
          return;
        }

        setTimeout(() => {
          opts.driver.moveNext();
        }, 100);
      },
      onPrevClick: (element, step, opts) => {
        if (typeof overrides?.onPrevClick === 'function') {
          overrides.onPrevClick(element, step, opts);
          return;
        }

        const prevClickEl = (step as any)?.onPrevClickEl as string | undefined;
        if (typeof prevClickEl === 'string' && prevClickEl.trim()) {
          queryFirstMatchingSelector(prevClickEl)?.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true }),
          );
        }

        if (!prevClickEl) {
          opts.driver.movePrevious();
          return;
        }

        setTimeout(() => {
          opts.driver.movePrevious();
        }, 100);
      },
      overlayClickBehavior: (element, step, opts) => {
        const behavior = overrides?.overlayClickBehavior;

        if (behavior === 'nextStep') {
          opts.driver.moveNext();
          return;
        }

        if (typeof behavior === 'function') {
          behavior(element, step, opts);
          return;
        }

        opts.driver.destroy();
      },
    }),
    [overrides, syncTutorialState],
  );

  const getTutorial = useCallback(() => {
    if (!tutorialRef.current) {
      tutorialRef.current = driver(
        createDefaultTutorialConfig({ ...overrides, ...configWithStoreHooks }),
      );
    }
    return tutorialRef.current;
  }, [configWithStoreHooks, overrides]);

  const startTour = useCallback(
    (steps: DriveStep[]) => {
      backdropWaitingFc(() => {
        syncTutorialState(true);

        const tutorial = getTutorial();
        tutorial.setConfig(
          createDefaultTutorialConfig({ ...overrides, ...configWithStoreHooks, steps }),
        );
        tutorial.setSteps(steps);

        setTimeout(() => {
          tutorial.drive();
        }, 100);
      });
    },
    [configWithStoreHooks, getTutorial, overrides, syncTutorialState],
  );

  const highlight = useCallback(
    (step: DriveStep) => {
      getTutorial().highlight(step);
    },
    [getTutorial],
  );

  const destroy = useCallback(() => {
    tutorialRef.current?.destroy();
    tutorialRef.current = null;
    syncTutorialState(false);
  }, [syncTutorialState]);

  useEffect(() => () => destroy(), [destroy]);

  return {
    startTour,
    highlight,
    destroy,
    getTutorial,
    isActive: () => tutorialRef.current?.isActive() ?? false,
    getActiveStep: () => tutorialRef.current?.getActiveStep(),
    getActiveIndex: () => tutorialRef.current?.getActiveIndex(),
  };
};
